<template>
  <div class="ds-flex ds-gap-4 ds-flex-wrap">
    <AkTooltipInformation
      v-for="filter in filtersWithASlot"
      :key="filter.name + filter.type"
      :is-enabled="filter.uid === highlightedFilterUid && highlightEnable"
      :show="filter.uid === highlightedFilterUid && highlightEnable"
      trigger="manual"
      close-button
      type="invert"
      placement="bottom"
      :custom-class="highlightCustomClass"
      :title="highlightTitle"
      :content="highlightContent"
      @close="$emit('highlightInteracted')"
      @apply-show="startTrackingHighlightScroll"
      @hide="stopTrackingHighlightScroll"
    >
      <AkHighlight
        :enable="filter.uid === highlightedFilterUid && highlightEnable"
        rounded="md"
        @click="filter.uid === highlightedFilterUid && $emit('highlightInteracted')"
      >
        <QuickFilter
          :title="filter.title"
          :sub-title="`${filter.count}`"
          :selected="filter.count > 0"
          :data-testid="filter.uid"
          @toggle="(open) => open && $emit('filterToggled', filter.trackingName)"
        >
          <template #badgeBefore>
            <AnkorstorePlusBadge v-if="filter.badgeType === 'aks_plus'" />
          </template>
          <template #title>
            <slot
              :name="`${filter.type}Title`"
              :filter="filter"
            />
          </template>
          <slot
            :name="filter.type"
            :filter="filter"
          />
        </QuickFilter>
      </AkHighlight>
    </AkTooltipInformation>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, useSlots } from 'vue';
import { AkHighlight, AkTooltipInformation } from '@ankorstore/design-system';
import QuickFilter from './quick-filter.vue';
import { Filter } from './types';
import AnkorstorePlusBadge from '@monolith/legacy/modules/promote-engage/subscription-program/UI/ankorstore-plus-badge.vue';
import { useHighlightScrollTracking } from '@core/composables/use-highlight-scroll-tracking';

const props = withDefaults(
  defineProps<{
    filters: Filter[];
    highlightedFilterUid?: string;
    highlightEnable?: boolean;
    highlightTitle?: string;
    highlightContent?: string;
    // Forwarded as-is to AkTooltipInformation's own custom-class prop, and used to scope the
    // scroll-tracking below to this specific tooltip's popper - callers styling the highlight
    // tooltip (see catalog.vue) should pass the same value used in their own CSS overrides.
    highlightCustomClass?: string;
  }>(),
  {
    highlightedFilterUid: undefined,
    highlightEnable: false,
    highlightTitle: undefined,
    highlightContent: undefined,
    highlightCustomClass: undefined,
  }
);
defineEmits<{ highlightInteracted: []; filterToggled: [trackingName: string] }>();
const slots = useSlots();

const filtersWithASlot = computed(() => props.filters.filter((filter) => filter.type in slots || `${filter.type}Title` in slots));

const { startTrackingScroll: startTrackingHighlightScroll, stopTrackingScroll: stopTrackingHighlightScroll } = useHighlightScrollTracking(
  `.ak-tooltip--${props.highlightCustomClass}.v-popper__popper--shown`
);
onBeforeUnmount(stopTrackingHighlightScroll);
</script>
